import React, { useState, useEffect, useRef, useCallback, useMemo, createContext, useContext } from "react";
import "@/App.css";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== CONTEXT ====================
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem('token');
    } catch (e) {
      console.warn('localStorage unavailable — using in-memory session');
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    try {
      localStorage.removeItem('token');
    } catch (e) {
      console.warn('localStorage unavailable during logout');
    }
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (err) {
      console.warn('Session validation failed — logging out');
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = useCallback(async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    try {
      localStorage.setItem('token', res.data.access_token);
    } catch (e) {
      console.warn('localStorage blocked (private mode / in-app browser) — continuing with in-memory session');
    }
    setToken(res.data.access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;
    setUser(res.data.user);
    return res.data;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password });
    try {
      localStorage.setItem('token', res.data.access_token);
    } catch (e) {
      console.warn('localStorage blocked (private mode / in-app browser) — continuing with in-memory session');
    }
    setToken(res.data.access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;
    setUser(res.data.user);
    return res.data;
  }, []);

  const refreshUser = useCallback(async () => {
    if (token) {
      await fetchUser();
    }
  }, [token, fetchUser]);

  const authValue = useMemo(
    () => ({ user, token, loading, login, register, logout, refreshUser }),
    [user, token, loading, login, register, logout, refreshUser]
  );

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
};

// ==================== ICONS ====================
const CameraIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/>
    <circle cx="12" cy="13" r="3"/>
  </svg>
);

const UploadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

const UserIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const HomeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

const HistoryIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
    <path d="M3 3v5h5"/>
    <path d="M12 7v5l4 2"/>
  </svg>
);

const TrophyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
    <path d="M4 22h16"/>
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
  </svg>
);

const FireIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>
  </svg>
);

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const FlipIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 3h5v5"/>
    <path d="M8 3H3v5"/>
    <path d="M12 22v-8.3a4 4 0 0 0-1.172-2.872L3 3"/>
    <path d="m15 9 6-6"/>
  </svg>
);

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
  </svg>
);

const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const ShareIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3"/>
    <circle cx="6" cy="12" r="3"/>
    <circle cx="18" cy="19" r="3"/>
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
  </svg>
);

const CrownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m2 4 3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14"/>
  </svg>
);

const StarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);

const LightbulbIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/>
    <path d="M9 18h6"/>
    <path d="M10 22h4"/>
  </svg>
);

const LogoutIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

const LockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

// ==================== COMPONENTS ====================

const ProgressCircle = ({ percentage, size = 120, strokeWidth = 10, color = "#000" }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (Math.min(percentage, 100) / 100) * circumference;

  return (
    <svg width={size} height={size} className="progress-circle">
      <circle
        className="progress-circle-bg"
        strokeWidth={strokeWidth}
        fill="transparent"
        r={radius}
        cx={size / 2}
        cy={size / 2}
      />
      <circle
        className="progress-circle-fill"
        strokeWidth={strokeWidth}
        fill="transparent"
        r={radius}
        cx={size / 2}
        cy={size / 2}
        style={{
          strokeDasharray: circumference,
          strokeDashoffset: offset,
          stroke: color,
        }}
      />
    </svg>
  );
};

// ==================== AUTH SCREENS ====================

// ==================== AVATAR ====================

const Avatar = ({ path, name, size = 40 }) => {
  const { token } = useAuth();
  if (path && token) {
    return (
      <img
        src={`${API}/files/${path}?auth=${token}`}
        alt={name || 'avatar'}
        className="avatar-img"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div className="avatar-fallback" style={{ width: size, height: size, fontSize: size * 0.42 }}>
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );
};

const AuthScreen = ({ onSuccess }) => {
  const [view, setView] = useState('login'); // 'login' | 'register' | 'forgot' | 'reset'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [emailExists, setEmailExists] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();

  const isLogin = view === 'login';

  const switchView = (v) => {
    setView(v);
    setError('');
    setInfo('');
    setEmailExists(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(name, email, password);
      }
      onSuccess();
    } catch (err) {
      const detail = err.response
        ? (err.response.data?.detail || 'Something went wrong')
        : 'Network error — please check your internet connection and try again.';
      if (detail === 'Email already registered') {
        setError('This email already has an account. Sign in below instead — or use "Forgot password?" if you don\'t remember your password.');
        setEmailExists(true);
      } else {
        setError(typeof detail === 'string' ? detail : 'Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setInfo(`A 6-digit reset code has been sent to ${email}. Check your inbox (and spam folder).`);
      setView('reset');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email,
        code: resetCode,
        new_password: newPassword
      });
      setResetCode('');
      setNewPassword('');
      switchView('login');
      setInfo('Password reset successful! Sign in with your new password.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (view === 'forgot' || view === 'reset') {
    return (
      <div className="auth-screen">
        <div className="auth-container">
          <div className="auth-logo">
            <div className="logo-icon">
              <CameraIcon />
            </div>
            <h1 className="logo-text">ZapCalories</h1>
            <p className="logo-tagline">
              {view === 'forgot' ? 'Reset your password' : 'Enter your reset code'}
            </p>
          </div>

          {view === 'forgot' ? (
            <form onSubmit={handleForgotPassword} className="auth-form" data-testid="forgot-password-form">
              <p className="auth-helper-text">
                Enter your registered email address and we'll send you a 6-digit code to reset your password.
              </p>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  data-testid="forgot-email-input"
                />
              </div>

              {error && <div className="error-message" data-testid="forgot-error-message">{error}</div>}

              <button type="submit" className="btn-primary" disabled={loading} data-testid="send-reset-code-btn">
                {loading ? 'Sending code...' : 'Send Reset Code'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="auth-form" data-testid="reset-password-form">
              {info && <div className="info-message" data-testid="reset-info-message">{info}</div>}
              <div className="form-group">
                <label>6-Digit Code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  required
                  minLength={6}
                  maxLength={6}
                  className="code-input"
                  data-testid="reset-code-input"
                />
              </div>
              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
              </div>

              {error && <div className="error-message" data-testid="reset-error-message">{error}</div>}

              <button type="submit" className="btn-primary" disabled={loading} data-testid="reset-password-btn">
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>

              <button
                type="button"
                className="link-button"
                onClick={() => switchView('forgot')}
                data-testid="resend-code-btn"
              >
                Didn't get the code? Send again
              </button>
            </form>
          )}

          <div className="auth-switch">
            <p>Remembered your password? <button onClick={() => switchView('login')} data-testid="back-to-login-btn">Sign in</button></p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-screen">
      <div className="auth-container">
        <div className="auth-logo">
          <div className="logo-icon">
            <CameraIcon />
          </div>
          <h1 className="logo-text">ZapCalories</h1>
          <p className="logo-tagline">AI-Powered Calorie Tracking</p>
        </div>

        <div className="auth-promo-banner">
          <span className="promo-badge">50% OFF</span>
          <span>Launch offer until Oct 26!</span>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {info && <div className="info-message" data-testid="auth-info-message">{info}</div>}
          {!isLogin && (
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                required={!isLogin}
                data-testid="signup-name-input"
              />
            </div>
          )}

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setEmailExists(false); }}
              placeholder="you@example.com"
              required
              data-testid="login-email-input"
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min 6 characters"
              required
              minLength={6}
              data-testid="login-password-input"
            />
          </div>

          {isLogin && (
            <div className="forgot-password-row">
              <button
                type="button"
                className="link-button"
                onClick={() => switchView('forgot')}
                data-testid="forgot-password-link"
              >
                Forgot password?
              </button>
            </div>
          )}

          {error && <div className="error-message" data-testid="auth-error-message">{error}</div>}

          {emailExists && !isLogin ? (
            <button
              type="button"
              className="btn-primary"
              onClick={() => switchView('login')}
              data-testid="switch-to-signin-btn"
            >
              Sign In With This Email
            </button>
          ) : (
            <button type="submit" className="btn-primary" disabled={loading} data-testid="auth-submit-btn">
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
            </button>
          )}
        </form>

        <div className="auth-switch">
          {isLogin ? (
            <p>Don't have an account? <button onClick={() => switchView('register')} data-testid="switch-to-signup-btn">Sign up</button></p>
          ) : (
            <p>Already have an account? <button onClick={() => switchView('login')} data-testid="switch-to-login-btn">Sign in</button></p>
          )}
        </div>

        <div className="auth-features">
          <div className="feature-item">
            <CameraIcon />
            <span>2 Free Scans</span>
          </div>
          <div className="feature-item">
            <FireIcon />
            <span>Streak Rewards</span>
          </div>
          <div className="feature-item">
            <TrophyIcon />
            <span>Achievements</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== DASHBOARD ====================

// ==================== WATER TRACKER ====================

const DropletIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z" />
  </svg>
);

const WaterTracker = () => {
  const [water, setWater] = useState({ glasses: 0, goal: 8 });
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    axios.get(`${API}/water-log`).then(res => setWater(res.data)).catch(console.error);
  }, []);

  const updateWater = async (delta) => {
    if (updating) return;
    setUpdating(true);
    try {
      const res = await axios.post(`${API}/water-log`, { delta });
      setWater(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="water-card" data-testid="water-tracker">
      <div className="water-header">
        <div className="water-title">
          <DropletIcon />
          <span>Water Intake</span>
        </div>
        <span className="water-count" data-testid="water-count">{water.glasses}/{water.goal} glasses</span>
      </div>
      <div className="water-glasses">
        {Array.from({ length: water.goal }).map((_, i) => (
          <div key={`glass-${i}`} className={`water-glass ${i < water.glasses ? 'filled' : ''}`}>
            <DropletIcon />
          </div>
        ))}
      </div>
      <div className="water-actions">
        <button className="water-btn" onClick={() => updateWater(-1)} disabled={updating || water.glasses === 0} data-testid="water-minus-btn">−</button>
        <button className="water-btn add" onClick={() => updateWater(1)} disabled={updating} data-testid="water-plus-btn">+ Add Glass</button>
      </div>
      {water.glasses >= water.goal && (
        <p className="water-goal-hit" data-testid="water-goal-message">Hydration goal crushed! 💧</p>
      )}
    </div>
  );
};

// ==================== WEEKLY CHART ====================

const WeeklyChart = ({ goalCalories }) => {
  const [days, setDays] = useState([]);

  useEffect(() => {
    axios.get(`${API}/weekly-progress`).then(res => setDays(res.data.days)).catch(console.error);
  }, []);

  if (!days.length) return null;

  const maxCal = Math.max(goalCalories || 0, ...days.map(d => d.total_calories), 1);

  return (
    <div className="weekly-card" data-testid="weekly-chart">
      <div className="weekly-header">
        <span className="weekly-title">This Week</span>
        <span className="weekly-subtitle">Calories per day</span>
      </div>
      <div className="weekly-bars">
        {days.map((d, i) => {
          const height = Math.max(4, (d.total_calories / maxCal) * 100);
          const isToday = i === days.length - 1;
          const overGoal = goalCalories && d.total_calories > goalCalories;
          return (
            <div key={d.date} className="weekly-bar-col">
              {d.total_calories > 0 && <span className="weekly-bar-value">{Math.round(d.total_calories)}</span>}
              <div className="weekly-bar-track">
                <div
                  className={`weekly-bar-fill ${overGoal ? 'over' : ''} ${isToday ? 'today' : ''}`}
                  style={{ height: `${height}%` }}
                />
              </div>
              <span className={`weekly-bar-label ${isToday ? 'today' : ''}`}>{d.day_label}</span>
            </div>
          );
        })}
      </div>
      {goalCalories > 0 && <p className="weekly-goal-note">Daily goal: {goalCalories} kcal</p>}
    </div>
  );
};

const Dashboard = ({ onNavigate, dailyProgress, tdeeData, subscriptionStatus, stats }) => {
  const { user } = useAuth();
  const hasProfile = user?.age && user?.height && user?.weight;
  
  const caloriePercentage = tdeeData ? (dailyProgress.total_calories / tdeeData.goal_calories) * 100 : 0;
  const proteinPercentage = tdeeData ? (dailyProgress.total_protein / tdeeData.protein_goal) * 100 : 0;
  const carbsPercentage = tdeeData ? (dailyProgress.total_carbs / tdeeData.carbs_goal) * 100 : 0;
  const fatPercentage = tdeeData ? (dailyProgress.total_fat / tdeeData.fat_goal) * 100 : 0;

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h1 className="heading-2">Hey, {user?.name?.split(' ')[0] || 'there'}!</h1>
          <p className="body-small">Track your nutrition goals</p>
        </div>
        <div className="streak-badge" onClick={() => onNavigate('achievements')}>
          <FireIcon />
          <span>{stats?.current_streak || 0}</span>
        </div>
      </div>

      {/* Subscription Banner */}
      {!subscriptionStatus?.has_subscription && (
        <div className="upgrade-banner" onClick={() => onNavigate('pricing')}>
          <div className="upgrade-content">
            <CrownIcon />
            <div>
              <strong>Free Scans: {subscriptionStatus?.free_scans_remaining || 0} left</strong>
              <p>Upgrade to unlock unlimited scans</p>
            </div>
          </div>
          <span className="upgrade-arrow">→</span>
        </div>
      )}

      {!hasProfile ? (
        <div className="setup-prompt">
          <div className="setup-icon">
            <UserIcon />
          </div>
          <h2 className="heading-3">Complete Your Profile</h2>
          <p className="body-medium">Set up your goals to get personalized calorie targets</p>
          <button className="btn-primary" onClick={() => onNavigate('profile')}>
            Set Up Profile
          </button>
        </div>
      ) : (
        <>
          <div className="calorie-card">
            <div className="calorie-circle-container">
              <ProgressCircle 
                percentage={caloriePercentage} 
                size={160} 
                strokeWidth={12}
                color={caloriePercentage > 100 ? "#ef4444" : "#000"}
              />
              <div className="calorie-text">
                <span className="calorie-current">{Math.round(dailyProgress.total_calories)}</span>
                <span className="calorie-divider">/</span>
                <span className="calorie-goal">{tdeeData?.goal_calories || 0}</span>
                <span className="calorie-label">kcal</span>
              </div>
            </div>
            <div className="calorie-info">
              <div className="info-item">
                <span className="info-label">BMR</span>
                <span className="info-value">{tdeeData?.bmr || 0} kcal</span>
              </div>
              <div className="info-item">
                <span className="info-label">TDEE</span>
                <span className="info-value">{tdeeData?.tdee || 0} kcal</span>
              </div>
              <div className="info-item">
                <span className="info-label">Goal</span>
                <span className="info-value capitalize">{tdeeData?.goal_type || 'maintain'}</span>
              </div>
            </div>
          </div>

          <div className="macros-grid">
            <div className="macro-card protein">
              <div className="macro-header">
                <span className="macro-name">Protein</span>
                <span className="macro-percentage">{Math.round(proteinPercentage)}%</span>
              </div>
              <div className="macro-bar">
                <div className="macro-bar-fill" style={{ width: `${Math.min(proteinPercentage, 100)}%` }} />
              </div>
              <div className="macro-values">
                <span>{Math.round(dailyProgress.total_protein)}g</span>
                <span className="macro-goal">/ {tdeeData?.protein_goal || 0}g</span>
              </div>
            </div>

            <div className="macro-card carbs">
              <div className="macro-header">
                <span className="macro-name">Carbs</span>
                <span className="macro-percentage">{Math.round(carbsPercentage)}%</span>
              </div>
              <div className="macro-bar">
                <div className="macro-bar-fill" style={{ width: `${Math.min(carbsPercentage, 100)}%` }} />
              </div>
              <div className="macro-values">
                <span>{Math.round(dailyProgress.total_carbs)}g</span>
                <span className="macro-goal">/ {tdeeData?.carbs_goal || 0}g</span>
              </div>
            </div>

            <div className="macro-card fat">
              <div className="macro-header">
                <span className="macro-name">Fat</span>
                <span className="macro-percentage">{Math.round(fatPercentage)}%</span>
              </div>
              <div className="macro-bar">
                <div className="macro-bar-fill" style={{ width: `${Math.min(fatPercentage, 100)}%` }} />
              </div>
              <div className="macro-values">
                <span>{Math.round(dailyProgress.total_fat)}g</span>
                <span className="macro-goal">/ {tdeeData?.fat_goal || 0}g</span>
              </div>
            </div>
          </div>

          {/* Water Tracker */}
          <WaterTracker />

          {/* Weekly Trend */}
          <WeeklyChart goalCalories={tdeeData?.goal_calories} />

          {/* Quick Actions */}
          <div className="quick-actions">
            <button className="action-btn" onClick={() => onNavigate('suggestions')}>
              <LightbulbIcon />
              <span>Meal Ideas</span>
            </button>
            <button className="action-btn" onClick={() => onNavigate('leaderboard')}>
              <TrophyIcon />
              <span>Leaderboard</span>
            </button>
            <button className="action-btn" onClick={() => onNavigate('reminders')} data-testid="reminders-quick-action">
              <BellIcon />
              <span>Reminders</span>
            </button>
            <button className="action-btn" onClick={() => onNavigate('challenges')} data-testid="challenges-quick-action">
              <SwordsIcon />
              <span>Challenges</span>
            </button>
          </div>

          <button className="scan-cta" onClick={() => onNavigate('scan')}>
            <CameraIcon />
            <span>Scan Food</span>
          </button>
        </>
      )}
    </div>
  );
};

// ==================== SCANNER ====================

const Scanner = ({ onFoodAnalyzed, onBack, subscriptionStatus }) => {
  const [isCapturing, setIsCapturing] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');
  const [mealType, setMealType] = useState('snack');
  const [streamActive, setStreamActive] = useState(false);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);

  const canScan = subscriptionStatus?.can_scan;

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setStreamActive(false);
    setIsCapturing(false);
  }, []);

  const startCamera = useCallback(async () => {
    if (!canScan) {
      setError('Scan limit reached. Please upgrade to continue.');
      return;
    }
    setError(null);
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setStreamActive(true);
        setIsCapturing(true);
      }
    } catch (err) {
      setError('Camera access denied. Try uploading an image instead.');
      setIsCapturing(false);
    }
  }, [facingMode, canScan]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const flipCamera = async () => {
    const newMode = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(newMode);
    if (isCapturing) {
      stopCamera();
      setTimeout(() => startCamera(), 100);
    }
  };

  const captureImage = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    const imageData = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedImage(imageData);
    stopCamera();
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!canScan) {
      setError('Scan limit reached. Please upgrade to continue.');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => setCapturedImage(event.target.result);
    reader.readAsDataURL(file);
  };

  const analyzeFood = async () => {
    if (!capturedImage) return;
    setIsAnalyzing(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/analyze-food`, { image_base64: capturedImage });
      setAnalysisResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Analysis failed. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const saveToLog = async () => {
    if (!analysisResult) return;
    try {
      await axios.post(`${API}/food-log`, {
        food_name: analysisResult.food_name,
        calories: analysisResult.calories,
        protein: analysisResult.protein,
        carbs: analysisResult.carbs,
        fat: analysisResult.fat,
        fiber: analysisResult.fiber,
        serving_size: analysisResult.serving_size,
        meal_type: mealType,
        image_base64: capturedImage
      });
      onFoodAnalyzed();
    } catch (err) {
      setError('Failed to save. Please try again.');
    }
  };

  const resetScanner = () => {
    setCapturedImage(null);
    setAnalysisResult(null);
    setError(null);
  };

  return (
    <div className="scanner-page">
      <div className="scanner-header">
        <button className="back-btn" onClick={onBack}><CloseIcon /></button>
        <h1 className="heading-3">Scan Food</h1>
        <div style={{ width: 40 }} />
      </div>

      {!canScan && (
        <div className="limit-banner">
          <LockIcon />
          <span>Scan limit reached. Upgrade to continue!</span>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {!capturedImage ? (
        <div className="scanner-view">
          <div className={`camera-container ${isCapturing && streamActive ? 'active' : 'hidden'}`}>
            <video ref={videoRef} autoPlay playsInline muted className="camera-preview" />
            <div className="camera-controls">
              <button className="capture-btn" onClick={captureImage}>
                <div className="capture-btn-inner" />
              </button>
              <button className="flip-btn" onClick={flipCamera}><FlipIcon /></button>
            </div>
          </div>
          <canvas ref={canvasRef} style={{ display: 'none' }} />

          {!isCapturing && (
            <div className="scanner-options">
              <button className="option-btn" onClick={startCamera} disabled={!canScan}>
                <CameraIcon />
                <span>Take Photo</span>
              </button>
              <button className="option-btn" onClick={() => fileInputRef.current?.click()} disabled={!canScan}>
                <UploadIcon />
                <span>Upload Image</span>
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} style={{ display: 'none' }} />
            </div>
          )}
        </div>
      ) : (
        <div className="analysis-view">
          <div className="captured-image-container">
            <img src={capturedImage} alt="Captured food" className="captured-image" />
          </div>

          {!analysisResult && !isAnalyzing && (
            <div className="analyze-actions">
              <button className="btn-secondary" onClick={resetScanner}>Retake</button>
              <button className="btn-primary" onClick={analyzeFood}>Analyze Food</button>
            </div>
          )}

          {isAnalyzing && (
            <div className="analyzing-state">
              <div className="spinner" />
              <p>Analyzing your food...</p>
            </div>
          )}

          {analysisResult && (
            <div className="analysis-result">
              <div className="result-header">
                <h2 className="heading-3">{analysisResult.food_name}</h2>
                <span className="confidence-badge">{Math.round(analysisResult.confidence * 100)}% confident</span>
              </div>

              <div className="nutrition-grid">
                <div className="nutrition-item calories">
                  <span className="nutrition-value">{Math.round(analysisResult.calories)}</span>
                  <span className="nutrition-label">Calories</span>
                </div>
                <div className="nutrition-item">
                  <span className="nutrition-value">{Math.round(analysisResult.protein)}g</span>
                  <span className="nutrition-label">Protein</span>
                </div>
                <div className="nutrition-item">
                  <span className="nutrition-value">{Math.round(analysisResult.carbs)}g</span>
                  <span className="nutrition-label">Carbs</span>
                </div>
                <div className="nutrition-item">
                  <span className="nutrition-value">{Math.round(analysisResult.fat)}g</span>
                  <span className="nutrition-label">Fat</span>
                </div>
              </div>

              <p className="serving-info">Serving: {analysisResult.serving_size}</p>
              <p className="analysis-details">{analysisResult.details}</p>

              <div className="meal-type-selector">
                <label className="body-small">Add to:</label>
                <div className="meal-buttons">
                  {['breakfast', 'lunch', 'dinner', 'snack'].map(type => (
                    <button key={type} className={`meal-btn ${mealType === type ? 'active' : ''}`} onClick={() => setMealType(type)}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="result-actions">
                <button className="btn-secondary" onClick={resetScanner}>Scan Again</button>
                <button className="btn-primary" onClick={saveToLog}>Add to Log</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== PROFILE ====================

const Profile = ({ onSave, onBack, onUpgrade }) => {
  const { user, logout, refreshUser } = useAuth();
  const [subInfo, setSubInfo] = useState(null);
  const [payments, setPayments] = useState([]);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    age: user?.age || '',
    gender: user?.gender || 'male',
    height: user?.height || '',
    weight: user?.weight || '',
    goal_weight: user?.goal_weight || '',
    activity_level: user?.activity_level || 'moderate'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef(null);

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await axios.post(`${API}/profile/avatar`, { image_base64: base64 });
      await refreshUser();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingAvatar(false);
      e.target.value = '';
    }
  };

  useEffect(() => {
    axios.get(`${API}/subscription/status`).then(res => setSubInfo(res.data)).catch(console.error);
    axios.get(`${API}/payments/history`).then(res => setPayments(res.data)).catch(console.error);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const data = {
        ...formData,
        age: parseInt(formData.age),
        height: parseFloat(formData.height),
        weight: parseFloat(formData.weight),
        goal_weight: parseFloat(formData.goal_weight)
      };
      await axios.put(`${API}/profile`, data);
      await refreshUser();
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-header">
        <button className="back-btn" onClick={onBack}><CloseIcon /></button>
        <h1 className="heading-3">Profile</h1>
        <button className="logout-btn" onClick={logout} data-testid="logout-btn"><LogoutIcon /></button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="profile-avatar-section" data-testid="profile-avatar-section">
        <div className="profile-avatar-wrap">
          <Avatar path={user?.avatar_path} name={user?.name} size={84} />
          <button
            className="avatar-edit-btn"
            onClick={() => avatarInputRef.current?.click()}
            disabled={uploadingAvatar}
            data-testid="avatar-upload-btn"
          >
            <CameraIcon />
          </button>
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleAvatarChange}
            data-testid="avatar-file-input"
          />
        </div>
        <p className="profile-avatar-name">{uploadingAvatar ? 'Uploading...' : user?.name}</p>
      </div>

      <div className="profile-stats">
        <div className="stat-item">
          <FireIcon />
          <span className="stat-value">{user?.current_streak || 0}</span>
          <span className="stat-label">Day Streak</span>
        </div>
        <div className="stat-item">
          <StarIcon />
          <span className="stat-value">{user?.total_points || 0}</span>
          <span className="stat-label">Points</span>
        </div>
        <div className="stat-item">
          <TrophyIcon />
          <span className="stat-value">Lv {user?.level || 1}</span>
          <span className="stat-label">Level</span>
        </div>
      </div>

      {/* My Subscription */}
      <div className="subscription-card" data-testid="my-subscription-card">
        <div className="subscription-card-header">
          <CrownIcon />
          <h2>My Subscription</h2>
        </div>
        {subInfo?.has_subscription ? (
          <>
            <div className="subscription-details">
              <div className="sub-detail-row">
                <span className="sub-label">Plan</span>
                <span className="sub-value capitalize" data-testid="subscription-plan">{subInfo.plan} · Premium</span>
              </div>
              <div className="sub-detail-row">
                <span className="sub-label">Status</span>
                <span className="sub-badge active">Active</span>
              </div>
              <div className="sub-detail-row">
                <span className="sub-label">Renews / Expires</span>
                <span className="sub-value" data-testid="subscription-expiry">
                  {subInfo.subscription_end ? new Date(subInfo.subscription_end).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                </span>
              </div>
              {subInfo.subscription_end && (
                <div className="sub-detail-row">
                  <span className="sub-label">Days left</span>
                  <span className="sub-value">{Math.max(0, Math.ceil((new Date(subInfo.subscription_end) - new Date()) / 86400000))} days</span>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="subscription-free">
            <p>You're on the <strong>Free plan</strong> — {subInfo ? Math.max(0, subInfo.free_scans_remaining) : 0} free scans left.</p>
            <button className="btn-primary" onClick={onUpgrade} data-testid="profile-upgrade-btn">Upgrade to Premium</button>
          </div>
        )}

        {payments.length > 0 && (
          <div className="payment-history" data-testid="payment-history">
            <h3>Payment History</h3>
            {payments.map(txn => (
              <div key={txn.id} className="payment-row">
                <div>
                  <span className="payment-plan capitalize">{txn.plan_id}</span>
                  <span className="payment-date">{new Date(txn.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                </div>
                <div className="payment-right">
                  <span className="payment-amount">₹{parseFloat(txn.amount).toFixed(0)}</span>
                  <span className={`payment-status ${txn.status}`}>{txn.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="refund-policy" data-testid="subscription-refund-policy">
          Refunds only within 24 hours of purchase. No refunds after 24 hours. Disputes subject to local jurisdiction.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="profile-form">
        <div className="form-group">
          <label>Name</label>
          <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Your name" />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Age</label>
            <input type="number" value={formData.age} onChange={(e) => setFormData({ ...formData, age: e.target.value })} placeholder="25" required />
          </div>
          <div className="form-group">
            <label>Gender</label>
            <select value={formData.gender} onChange={(e) => setFormData({ ...formData, gender: e.target.value })}>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Height (cm)</label>
          <input type="number" value={formData.height} onChange={(e) => setFormData({ ...formData, height: e.target.value })} placeholder="170" required />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Current Weight (kg)</label>
            <input type="number" step="0.1" value={formData.weight} onChange={(e) => setFormData({ ...formData, weight: e.target.value })} placeholder="70" required />
          </div>
          <div className="form-group">
            <label>Goal Weight (kg)</label>
            <input type="number" step="0.1" value={formData.goal_weight} onChange={(e) => setFormData({ ...formData, goal_weight: e.target.value })} placeholder="65" required />
          </div>
        </div>

        <div className="form-group">
          <label>Activity Level</label>
          <select value={formData.activity_level} onChange={(e) => setFormData({ ...formData, activity_level: e.target.value })}>
            <option value="sedentary">Sedentary (little/no exercise)</option>
            <option value="light">Light (1-3 days/week)</option>
            <option value="moderate">Moderate (3-5 days/week)</option>
            <option value="active">Active (6-7 days/week)</option>
            <option value="very_active">Very Active (physical job)</option>
          </select>
        </div>

        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? 'Saving...' : 'Save Profile'}
        </button>
      </form>
    </div>
  );
};

// ==================== HISTORY ====================

const History = ({ foodLogs, onDelete, onShare, onBack }) => {
  const { token } = useAuth();
  const groupedLogs = foodLogs.reduce((acc, log) => {
    const date = new Date(log.logged_at).toLocaleDateString();
    if (!acc[date]) acc[date] = [];
    acc[date].push(log);
    return acc;
  }, {});

  return (
    <div className="history-page">
      <div className="history-header">
        <button className="back-btn" onClick={onBack}><CloseIcon /></button>
        <h1 className="heading-3">Food History</h1>
        <div style={{ width: 40 }} />
      </div>

      {Object.keys(groupedLogs).length === 0 ? (
        <div className="empty-state">
          <HistoryIcon />
          <p>No food logged yet</p>
          <span className="body-small">Start scanning your meals!</span>
        </div>
      ) : (
        <div className="history-list">
          {Object.entries(groupedLogs).map(([date, logs]) => (
            <div key={date} className="date-group">
              <h3 className="date-header">{date}</h3>
              {logs.map(log => (
                <div key={log.id} className="food-log-item">
                  {log.image_path && token && (
                    <img
                      src={`${API}/files/${log.image_path}?auth=${token}`}
                      alt={log.food_name}
                      className="log-thumb"
                      loading="lazy"
                      data-testid="food-log-image"
                    />
                  )}
                  <div className="log-info">
                    <span className="log-name">{log.food_name}</span>
                    <span className="log-meal">{log.meal_type}</span>
                  </div>
                  <div className="log-nutrition">
                    <span className="log-calories">{Math.round(log.calories)} kcal</span>
                    <span className="log-macros">P: {Math.round(log.protein)}g | C: {Math.round(log.carbs)}g | F: {Math.round(log.fat)}g</span>
                  </div>
                  <div className="log-actions">
                    <button className="share-btn" onClick={() => onShare(log.id)}><ShareIcon /></button>
                    <button className="delete-btn" onClick={() => onDelete(log.id)}><TrashIcon /></button>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== ACHIEVEMENTS ====================

// ==================== SHARE STATS ====================

const buildShareText = (stats) =>
  `🔥 I'm crushing my nutrition goals on ZapCalories!\n🏆 Level ${stats?.level || 1} • ⭐ ${stats?.total_points || 0} points • 🔥 ${stats?.current_streak || 0}-day streak • 🏅 Rank #${stats?.rank || '-'}\nScan your food with AI and try to beat me! 💪`;

const SHARE_URL = 'https://zapcalories.com';

const ShareStats = ({ stats, label = 'Share My Stats' }) => {
  const [showSheet, setShowSheet] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareText = buildShareText(stats);
  const encoded = encodeURIComponent(`${shareText}\n${SHARE_URL}`);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'My ZapCalories Stats', text: shareText, url: SHARE_URL });
        return;
      } catch (e) {
        if (e.name === 'AbortError') return;
      }
    }
    setShowSheet(true);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText}\n${SHARE_URL}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <>
      <button className="btn-primary share-stats-btn" onClick={handleShare} data-testid="share-stats-btn">
        <ShareIcon />
        <span>{label}</span>
      </button>

      {showSheet && (
        <div className="share-sheet-overlay" onClick={() => setShowSheet(false)} data-testid="share-sheet">
          <div className="share-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="share-sheet-handle" />
            <h3>Share your achievement</h3>
            <div className="share-options">
              <a className="share-option whatsapp" href={`https://wa.me/?text=${encoded}`} target="_blank" rel="noreferrer" data-testid="share-whatsapp">WhatsApp</a>
              <a className="share-option twitter" href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(SHARE_URL)}`} target="_blank" rel="noreferrer" data-testid="share-twitter">X / Twitter</a>
              <a className="share-option facebook" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SHARE_URL)}&quote=${encodeURIComponent(shareText)}`} target="_blank" rel="noreferrer" data-testid="share-facebook">Facebook</a>
              <a className="share-option telegram" href={`https://t.me/share/url?url=${encodeURIComponent(SHARE_URL)}&text=${encodeURIComponent(shareText)}`} target="_blank" rel="noreferrer" data-testid="share-telegram">Telegram</a>
              <button className="share-option copy" onClick={copyToClipboard} data-testid="share-copy">
                {copied ? 'Copied! ✓' : 'Copy Text'}
              </button>
            </div>
            <button className="btn-secondary" onClick={() => setShowSheet(false)} data-testid="share-sheet-close">Close</button>
          </div>
        </div>
      )}
    </>
  );
};

const BragCard = ({ stats, userName }) => (
  <div className="brag-card" data-testid="brag-card">
    <div className="brag-card-header">
      <span className="brag-app">⚡ ZapCalories</span>
      <span className="brag-rank">Rank #{stats?.rank || '-'}</span>
    </div>
    <p className="brag-name">{userName}</p>
    <div className="brag-stats">
      <div className="brag-stat">
        <span className="brag-stat-value">Lv {stats?.level || 1}</span>
        <span className="brag-stat-label">Level</span>
      </div>
      <div className="brag-stat">
        <span className="brag-stat-value">{stats?.total_points || 0}</span>
        <span className="brag-stat-label">Points</span>
      </div>
      <div className="brag-stat">
        <span className="brag-stat-value">{stats?.current_streak || 0}🔥</span>
        <span className="brag-stat-label">Streak</span>
      </div>
      <div className="brag-stat">
        <span className="brag-stat-value">{stats?.achievements_unlocked || 0}</span>
        <span className="brag-stat-label">Badges</span>
      </div>
    </div>
    <p className="brag-footer">Think you can beat me? zapcalories.com</p>
  </div>
);

const Achievements = ({ onBack }) => {
  const { user } = useAuth();
  const [achievements, setAchievements] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [achRes, statsRes] = await Promise.all([
          axios.get(`${API}/achievements`),
          axios.get(`${API}/stats`)
        ]);
        setAchievements(achRes.data);
        setStats(statsRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const unlocked = achievements.filter(a => a.unlocked);
  const locked = achievements.filter(a => !a.unlocked);

  if (loading) {
    return <div className="loading-screen"><div className="spinner" /></div>;
  }

  return (
    <div className="achievements-page">
      <div className="page-header">
        <button className="back-btn" onClick={onBack}><CloseIcon /></button>
        <h1 className="heading-3">Achievements</h1>
        <div style={{ width: 40 }} />
      </div>

      <div className="achievements-stats">
        <div className="big-stat">
          <span className="big-stat-value">{stats?.total_points || 0}</span>
          <span className="big-stat-label">Total Points</span>
        </div>
        <div className="stats-row">
          <div className="mini-stat">
            <FireIcon />
            <span>{stats?.current_streak || 0} day streak</span>
          </div>
          <div className="mini-stat">
            <TrophyIcon />
            <span>{unlocked.length}/{achievements.length} badges</span>
          </div>
        </div>
      </div>

      <BragCard stats={stats} userName={user?.name || 'ZapCalories User'} />
      <div className="share-stats-wrap">
        <ShareStats stats={stats} label="Share My Stats" />
      </div>

      <div className="achievements-section">
        <h2 className="section-title">Unlocked ({unlocked.length})</h2>
        <div className="achievements-grid">
          {unlocked.map(ach => (
            <div key={ach.id} className="achievement-card unlocked">
              <div className="achievement-icon"><TrophyIcon /></div>
              <div className="achievement-info">
                <span className="achievement-name">{ach.name}</span>
                <span className="achievement-desc">{ach.description}</span>
                <span className="achievement-points">+{ach.points} pts</span>
              </div>
            </div>
          ))}
          {unlocked.length === 0 && <p className="empty-text">Complete tasks to unlock badges!</p>}
        </div>
      </div>

      <div className="achievements-section">
        <h2 className="section-title">Locked ({locked.length})</h2>
        <div className="achievements-grid">
          {locked.map(ach => (
            <div key={ach.id} className="achievement-card locked">
              <div className="achievement-icon"><LockIcon /></div>
              <div className="achievement-info">
                <span className="achievement-name">{ach.name}</span>
                <span className="achievement-desc">{ach.description}</span>
                <span className="achievement-points">+{ach.points} pts</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ==================== LEADERBOARD ====================

const Leaderboard = ({ onBack, onChallenge }) => {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const [lbRes, statsRes] = await Promise.all([
          axios.get(`${API}/leaderboard`),
          axios.get(`${API}/stats`)
        ]);
        setLeaderboard(lbRes.data);
        setStats(statsRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  }, []);

  if (loading) {
    return <div className="loading-screen"><div className="spinner" /></div>;
  }

  return (
    <div className="leaderboard-page">
      <div className="page-header">
        <button className="back-btn" onClick={onBack}><CloseIcon /></button>
        <h1 className="heading-3">Leaderboard</h1>
        <div style={{ width: 40 }} />
      </div>

      <div className="leaderboard-list">
        {leaderboard.map((entry, idx) => (
          <div key={entry.user_id} className={`leaderboard-item ${entry.user_id === user?.id ? 'current-user' : ''}`}>
            <div className="rank-badge">
              {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${entry.rank}`}
            </div>
            <Avatar path={entry.avatar_path} name={entry.name} size={36} />
            <div className="leader-info">
              <span className="leader-name">{entry.name}</span>
              <span className="leader-stats">Lv {entry.level} • {entry.streak} day streak</span>
            </div>
            <div className="leader-points">
              <span>{entry.points}</span>
              <span className="points-label">pts</span>
            </div>
          </div>
        ))}
        {leaderboard.length === 0 && (
          <div className="empty-state"><p>No users yet. Be the first!</p></div>
        )}
      </div>

      <div className="share-stats-wrap">
        <ShareStats stats={stats} label="Share My Rank" />
        <button className="btn-secondary challenge-friend-btn" onClick={onChallenge} data-testid="leaderboard-challenge-btn">
          ⚔️ Challenge a Friend
        </button>
      </div>
    </div>
  );
};

// ==================== PRICING ====================

const Pricing = ({ onBack }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(null);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await axios.get(`${API}/pricing`);
        setPlans(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchPricing();
  }, []);

  const handleSubscribe = async (planId) => {
    setActivating(planId);
    try {
      const res = await axios.post(`${API}/payments/payu/initiate`, { plan_id: planId });
      const { payment_url, params } = res.data;

      // Build a hidden form and submit to PayU hosted checkout
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = payment_url;
      Object.entries(params).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to start payment. Please try again.');
      setActivating(null);
    }
  };

  if (loading) {
    return <div className="loading-screen"><div className="spinner" /></div>;
  }

  const isPromo = plans[0]?.is_promo_active;

  return (
    <div className="pricing-page">
      <div className="page-header">
        <button className="back-btn" onClick={onBack}><CloseIcon /></button>
        <h1 className="heading-3">Upgrade</h1>
        <div style={{ width: 40 }} />
      </div>

      {isPromo && (
        <div className="promo-banner">
          <CrownIcon />
          <div>
            <strong>50% OFF Launch Sale!</strong>
            <p>Offer ends October 26, 2026</p>
          </div>
        </div>
      )}

      <div className="pricing-cards">
        {plans.map(plan => (
          <div key={plan.id} className={`pricing-card ${plan.id === 'annual' ? 'featured' : ''}`}>
            {plan.id === 'annual' && <div className="best-value">Best Value</div>}
            <h3 className="plan-name">{plan.name}</h3>
            <div className="plan-price">
              {isPromo && (
                <span className="original-price">₹{plan.price.toFixed(0)}</span>
              )}
              <span className="current-price">₹{isPromo ? plan.promo_price.toFixed(0) : plan.price.toFixed(0)}</span>
              <span className="price-period">/{plan.id === 'monthly' ? 'mo' : plan.id === 'quarterly' ? '3mo' : 'yr'}</span>
            </div>
            <ul className="plan-features">
              <li><CheckIcon /> {plan.scans_per_month === -1 ? 'Unlimited' : plan.scans_per_month} scans/month</li>
              <li><CheckIcon /> AI-powered analysis</li>
              <li><CheckIcon /> Macro tracking</li>
              <li><CheckIcon /> Streak rewards</li>
              {plan.id === 'annual' && <li><CheckIcon /> Priority support</li>}
            </ul>
            <button 
              className="btn-primary" 
              onClick={() => handleSubscribe(plan.id)}
              disabled={activating === plan.id}
              data-testid={`subscribe-${plan.id}-btn`}
            >
              {activating === plan.id ? 'Redirecting to PayU...' : 'Subscribe'}
            </button>
          </div>
        ))}
      </div>

      <p className="pricing-note">Secure payments powered by PayU. Cards, UPI, Net Banking & Wallets accepted.</p>
      <p className="refund-policy" data-testid="refund-policy">
        <strong>Refund Policy:</strong> Cancellations are eligible for a refund only within 24 hours of purchase.
        No refunds are provided for cancellations after 24 hours. All disputes are subject to local jurisdiction.
      </p>
    </div>
  );
};

// ==================== MEAL SUGGESTIONS ====================

const MealSuggestions = ({ onBack }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mealType, setMealType] = useState('lunch');

  useEffect(() => {
    fetchSuggestions();
  }, [mealType]);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/meal-suggestions?meal_type=${mealType}`);
      setSuggestions(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="suggestions-page">
      <div className="page-header">
        <button className="back-btn" onClick={onBack}><CloseIcon /></button>
        <h1 className="heading-3">Meal Ideas</h1>
        <div style={{ width: 40 }} />
      </div>

      <div className="meal-type-tabs">
        {['breakfast', 'lunch', 'dinner', 'snack'].map(type => (
          <button 
            key={type} 
            className={`tab-btn ${mealType === type ? 'active' : ''}`}
            onClick={() => setMealType(type)}
          >
            {type.charAt(0).toUpperCase() + type.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading-state"><div className="spinner" /><p>Getting AI suggestions...</p></div>
      ) : suggestions.length === 0 ? (
        <div className="empty-state">
          <LightbulbIcon />
          <p>No suggestions available</p>
          <span className="body-small">Complete your profile and log some meals first</span>
        </div>
      ) : (
        <div className="suggestions-list">
          {suggestions.map((meal, idx) => (
            <div key={`${meal.meal_name}-${idx}`} className="suggestion-card">
              <h3 className="meal-name">{meal.meal_name}</h3>
              <p className="meal-description">{meal.description}</p>
              <div className="meal-macros">
                <span className="macro-tag calories">{Math.round(meal.calories)} kcal</span>
                <span className="macro-tag">P: {Math.round(meal.protein)}g</span>
                <span className="macro-tag">C: {Math.round(meal.carbs)}g</span>
                <span className="macro-tag">F: {Math.round(meal.fat)}g</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== REMINDERS ====================

const BellIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);

const computeWaterTimes = (start, end, intervalHours) => {
  const times = [];
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let cur = sh * 60 + sm;
  const endMin = eh * 60 + em;
  while (cur <= endMin) {
    times.push(`${String(Math.floor(cur / 60)).padStart(2, '0')}:${String(cur % 60).padStart(2, '0')}`);
    cur += intervalHours * 60;
  }
  return times;
};

const fireNotification = (title, body) => {
  if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
    try {
      new Notification(title, { body, icon: '/favicon.ico' });
      return true;
    } catch (e) {
      console.error(e);
    }
  }
  return false;
};

const useReminderScheduler = (user, refreshKey) => {
  const settingsRef = useRef(null);
  const firedRef = useRef({});

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/reminders`).then(res => { settingsRef.current = res.data; }).catch(console.error);
  }, [user, refreshKey]);

  useEffect(() => {
    if (!user) return;
    const tick = () => {
      const s = settingsRef.current;
      if (!s) return;
      const now = new Date();
      const hhmm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const today = now.toDateString();

      if (s.water_enabled && computeWaterTimes(s.water_start, s.water_end, s.water_interval_hours).includes(hhmm)) {
        const key = `water-${hhmm}-${today}`;
        if (!firedRef.current[key]) {
          firedRef.current[key] = true;
          fireNotification('💧 Water Reminder', 'Time to drink a glass of water! Log it in ZapCalories.');
        }
      }

      (s.meals || []).forEach(m => {
        if (m.enabled && m.time === hhmm) {
          const key = `meal-${m.type}-${today}`;
          if (!firedRef.current[key]) {
            firedRef.current[key] = true;
            const label = m.type.charAt(0).toUpperCase() + m.type.slice(1);
            fireNotification(`🍽️ ${label} time!`, `Don't forget to scan & log your ${m.type} on ZapCalories.`);
          }
        }
      });
    };
    tick();
    const id = setInterval(tick, 20000);
    return () => clearInterval(id);
  }, [user]);
};

const RemindersPage = ({ onBack, onSaved }) => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );

  useEffect(() => {
    axios.get(`${API}/reminders`).then(res => setSettings(res.data)).catch(console.error);
  }, []);

  const requestPermission = async () => {
    if (typeof Notification === 'undefined') return;
    const result = await Notification.requestPermission();
    setPermission(result);
    if (result === 'granted') {
      fireNotification('🔔 Notifications enabled!', 'ZapCalories will remind you to drink water and log meals.');
    }
  };

  const sendTest = () => {
    const ok = fireNotification('🔔 Test notification', 'Reminders are working! Stay hydrated 💧');
    if (!ok) alert('Please enable notifications first.');
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/reminders`, settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      onSaved();
    } catch (err) {
      alert('Failed to save reminders');
    } finally {
      setSaving(false);
    }
  };

  const updateMeal = (idx, field, value) => {
    const meals = settings.meals.map((m, i) => (i === idx ? { ...m, [field]: value } : m));
    setSettings({ ...settings, meals });
  };

  if (!settings) {
    return <div className="loading-screen"><div className="spinner" /></div>;
  }

  return (
    <div className="reminders-page" data-testid="reminders-page">
      <div className="page-header">
        <button className="back-btn" onClick={onBack} data-testid="reminders-back-btn"><CloseIcon /></button>
        <h1 className="heading-3">Reminders</h1>
        <div style={{ width: 40 }} />
      </div>

      {permission !== 'granted' && (
        <div className="notification-banner" data-testid="notification-permission-banner">
          <BellIcon />
          <div className="notification-banner-text">
            <strong>Enable notifications</strong>
            <p>{permission === 'denied'
              ? 'Notifications are blocked. Allow them in your browser settings for this site.'
              : 'Allow notifications so ZapCalories can remind you.'}</p>
          </div>
          {permission === 'default' && (
            <button className="btn-primary small" onClick={requestPermission} data-testid="enable-notifications-btn">Enable</button>
          )}
        </div>
      )}

      <div className="reminder-card" data-testid="water-reminder-card">
        <div className="reminder-card-header">
          <div className="reminder-title water">
            <DropletIcon />
            <span>Water Reminders</span>
          </div>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.water_enabled}
              onChange={(e) => setSettings({ ...settings, water_enabled: e.target.checked })}
              data-testid="water-reminder-toggle"
            />
            <span className="toggle-slider" />
          </label>
        </div>
        {settings.water_enabled && (
          <div className="reminder-config">
            <div className="config-row">
              <label>Remind me every</label>
              <select
                value={settings.water_interval_hours}
                onChange={(e) => setSettings({ ...settings, water_interval_hours: parseInt(e.target.value) })}
                data-testid="water-interval-select"
              >
                <option value={1}>1 hour</option>
                <option value={2}>2 hours</option>
                <option value={3}>3 hours</option>
                <option value={4}>4 hours</option>
              </select>
            </div>
            <div className="config-row">
              <label>From</label>
              <input type="time" value={settings.water_start} onChange={(e) => setSettings({ ...settings, water_start: e.target.value })} data-testid="water-start-input" />
              <label>to</label>
              <input type="time" value={settings.water_end} onChange={(e) => setSettings({ ...settings, water_end: e.target.value })} data-testid="water-end-input" />
            </div>
          </div>
        )}
      </div>

      <div className="reminder-card" data-testid="meal-reminders-card">
        <div className="reminder-card-header">
          <div className="reminder-title">
            <BellIcon />
            <span>Meal Reminders</span>
          </div>
        </div>
        {settings.meals.map((meal, idx) => (
          <div key={meal.type} className="meal-reminder-row" data-testid={`meal-reminder-${meal.type}`}>
            <span className="meal-reminder-name capitalize">{meal.type}</span>
            <input
              type="time"
              value={meal.time}
              onChange={(e) => updateMeal(idx, 'time', e.target.value)}
              disabled={!meal.enabled}
              data-testid={`meal-time-${meal.type}`}
            />
            <label className="toggle">
              <input
                type="checkbox"
                checked={meal.enabled}
                onChange={(e) => updateMeal(idx, 'enabled', e.target.checked)}
                data-testid={`meal-toggle-${meal.type}`}
              />
              <span className="toggle-slider" />
            </label>
          </div>
        ))}
      </div>

      <button className="btn-primary reminders-save-btn" onClick={save} disabled={saving} data-testid="save-reminders-btn">
        {saving ? 'Saving...' : saved ? 'Saved ✓' : 'Save Reminders'}
      </button>

      <button className="btn-secondary reminders-test-btn" onClick={sendTest} data-testid="test-notification-btn">
        Send Test Notification
      </button>

      <p className="reminders-note">Reminders fire while ZapCalories is open in your browser or installed on your home screen.</p>
    </div>
  );
};

// ==================== CHALLENGES ====================

const SwordsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5" />
    <line x1="13" y1="19" x2="19" y2="13" />
    <line x1="16" y1="16" x2="20" y2="20" />
    <line x1="19" y1="21" x2="21" y2="19" />
    <polyline points="14.5 6.5 18 3 21 3 21 6 17.5 9.5" />
    <line x1="5" y1="14" x2="9" y2="18" />
    <line x1="7" y1="17" x2="4" y2="20" />
    <line x1="3" y1="19" x2="5" y2="21" />
  </svg>
);

const CHALLENGE_SHARE_TEXT = "⚔️ I challenge you to a 7-day streak battle on ZapCalories! Scan & log your meals daily with AI — let's see who stays consistent. Join my challenge:";

const ChallengesPage = ({ onBack }) => {
  const { user } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [shareId, setShareId] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchChallenges = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/challenges`);
      setChallenges(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchChallenges(); }, [fetchChallenges]);

  const challengeLink = (id) => `${window.location.origin}/?challenge=${id}`;

  const openShare = async (id) => {
    const url = challengeLink(id);
    if (navigator.share) {
      try {
        await navigator.share({ title: 'ZapCalories Challenge', text: CHALLENGE_SHARE_TEXT, url });
        return;
      } catch (e) {
        if (e.name === 'AbortError') return;
      }
    }
    setShareId(id);
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/challenges`);
      await fetchChallenges();
      openShare(res.data.challenge_id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create challenge');
    } finally {
      setCreating(false);
    }
  };

  const openDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/challenges/${id}`);
      setSelected(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const copyLink = async (id) => {
    try {
      await navigator.clipboard.writeText(`${CHALLENGE_SHARE_TEXT}\n${challengeLink(id)}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) {
    return <div className="loading-screen"><div className="spinner" /></div>;
  }

  const fmtDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

  if (selected) {
    return (
      <div className="challenges-page" data-testid="challenge-detail">
        <div className="page-header">
          <button className="back-btn" onClick={() => setSelected(null)} data-testid="challenge-detail-back"><CloseIcon /></button>
          <h1 className="heading-3">Streak Battle</h1>
          <div style={{ width: 40 }} />
        </div>

        <div className="challenge-hero">
          <SwordsIcon />
          <h2>{selected.creator_name}'s 7-Day Challenge</h2>
          <p>{fmtDate(selected.start_date)} – {fmtDate(selected.end_date)}</p>
          {selected.status === 'active' ? (
            <span className="challenge-chip active" data-testid="challenge-days-remaining">
              {selected.days_remaining} day{selected.days_remaining !== 1 ? 's' : ''} left
            </span>
          ) : (
            <span className="challenge-chip ended">
              Ended — {selected.standings[0] ? `🏆 ${selected.standings[0].name} wins!` : 'No entries'}
            </span>
          )}
        </div>

        <div className="challenge-standings" data-testid="challenge-standings">
          {selected.standings.map(s => (
            <div key={s.user_id} className={`leaderboard-item ${s.user_id === user?.id ? 'current-user' : ''}`}>
              <div className="rank-badge">
                {s.rank === 1 ? '👑' : `#${s.rank}`}
              </div>
              <Avatar path={s.avatar_path} name={s.name} size={36} />
              <div className="leader-info">
                <span className="leader-name">{s.name}{s.user_id === user?.id ? ' (You)' : ''}</span>
                <span className="leader-stats">{s.meals_logged} meals logged</span>
              </div>
              <div className="leader-points">
                <span>{s.days_logged}/7</span>
                <span className="points-label">days</span>
              </div>
            </div>
          ))}
        </div>

        {selected.status === 'active' && (
          <button className="btn-primary challenge-invite-btn" onClick={() => openShare(selected.id)} data-testid="challenge-invite-btn">
            <ShareIcon />
            <span>Invite More Friends</span>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="challenges-page" data-testid="challenges-page">
      <div className="page-header">
        <button className="back-btn" onClick={onBack} data-testid="challenges-back-btn"><CloseIcon /></button>
        <h1 className="heading-3">Challenges</h1>
        <div style={{ width: 40 }} />
      </div>

      <div className="challenge-cta">
        <div className="challenge-cta-icon"><SwordsIcon /></div>
        <h2>7-Day Streak Battle</h2>
        <p>Challenge your friends: whoever logs meals on the most days in 7 days wins. Winner takes the bragging rights!</p>
        <button className="btn-primary" onClick={handleCreate} disabled={creating} data-testid="create-challenge-btn">
          {creating ? 'Creating...' : '⚔️ Challenge a Friend'}
        </button>
      </div>

      {challenges.length > 0 && (
        <div className="challenge-list" data-testid="challenge-list">
          <h2 className="section-title">My Battles</h2>
          {challenges.map(ch => (
            <button key={ch.id} className="challenge-card" onClick={() => openDetail(ch.id)} data-testid={`challenge-card-${ch.id}`}>
              <div className="challenge-card-left">
                <span className="challenge-card-title">{ch.creator_name}'s Challenge</span>
                <span className="challenge-card-sub">
                  {fmtDate(ch.start_date)} – {fmtDate(ch.end_date)} • {ch.participant_count} player{ch.participant_count !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="challenge-card-right">
                <span className={`challenge-chip ${ch.status}`}>{ch.status === 'active' ? 'Active' : 'Ended'}</span>
                {ch.my_rank && <span className="challenge-my-rank">#{ch.my_rank} • {ch.my_days}/7 days</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      {shareId && (
        <div className="share-sheet-overlay" onClick={() => setShareId(null)} data-testid="challenge-share-sheet">
          <div className="share-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="share-sheet-handle" />
            <h3>Send your challenge</h3>
            <div className="share-options">
              <a className="share-option whatsapp" href={`https://wa.me/?text=${encodeURIComponent(`${CHALLENGE_SHARE_TEXT}\n${challengeLink(shareId)}`)}`} target="_blank" rel="noreferrer" data-testid="challenge-share-whatsapp">WhatsApp</a>
              <a className="share-option telegram" href={`https://t.me/share/url?url=${encodeURIComponent(challengeLink(shareId))}&text=${encodeURIComponent(CHALLENGE_SHARE_TEXT)}`} target="_blank" rel="noreferrer" data-testid="challenge-share-telegram">Telegram</a>
              <a className="share-option twitter" href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(CHALLENGE_SHARE_TEXT)}&url=${encodeURIComponent(challengeLink(shareId))}`} target="_blank" rel="noreferrer" data-testid="challenge-share-twitter">X / Twitter</a>
              <button className="share-option copy" onClick={() => copyLink(shareId)} data-testid="challenge-share-copy">
                {copied ? 'Copied! ✓' : 'Copy Invite Link'}
              </button>
            </div>
            <button className="btn-secondary" onClick={() => setShareId(null)} data-testid="challenge-share-close">Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== MAIN APP ====================

function AppContent() {
  const { user, loading: authLoading, refreshUser } = useAuth();
  const [currentPage, setCurrentPage] = useState('home');
  const [dailyProgress, setDailyProgress] = useState({ total_calories: 0, total_protein: 0, total_carbs: 0, total_fat: 0, meal_count: 0 });
  const [tdeeData, setTdeeData] = useState(null);
  const [foodLogs, setFoodLogs] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentResult, setPaymentResult] = useState(null);
  const [reminderRefresh, setReminderRefresh] = useState(0);
  const [pendingChallenge, setPendingChallenge] = useState(null);
  const [challengeJoinMsg, setChallengeJoinMsg] = useState(null);

  useReminderScheduler(user, reminderRefresh);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const payment = searchParams.get('payment');
    const challengeCode = searchParams.get('challenge');
    if (payment) setPaymentResult(payment);
    if (challengeCode) setPendingChallenge(challengeCode);
    if (payment || challengeCode) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (!user || !pendingChallenge) return;
    const code = pendingChallenge;
    setPendingChallenge(null);
    axios.post(`${API}/challenges/${code}/join`)
      .then(res => {
        setChallengeJoinMsg(res.data.message === 'already_joined'
          ? "You're already in this challenge — game on! ⚔️"
          : `Challenge accepted! You joined ${res.data.creator_name}'s 7-day streak battle ⚔️`);
        setCurrentPage('challenges');
      })
      .catch(err => {
        setChallengeJoinMsg(err.response?.data?.detail || 'Could not join the challenge.');
      });
  }, [user, pendingChallenge]);

  const fetchData = useCallback(async () => {
    if (!user) return;
    try {
      const [progressRes, logsRes, subRes, statsRes] = await Promise.all([
        axios.get(`${API}/daily-progress`),
        axios.get(`${API}/food-log?limit=50`),
        axios.get(`${API}/subscription/status`),
        axios.get(`${API}/stats`)
      ]);
      setDailyProgress(progressRes.data);
      setFoodLogs(logsRes.data);
      setSubscriptionStatus(subRes.data);
      setStats(statsRes.data);

      if (user.age && user.height && user.weight) {
        const tdeeRes = await axios.get(`${API}/calculate-tdee`);
        setTdeeData(tdeeRes.data);
      }
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [user, fetchData]);

  const handleDeleteLog = async (logId) => {
    try {
      await axios.delete(`${API}/food-log/${logId}`);
      fetchData();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleShareMeal = async (logId) => {
    try {
      const res = await axios.post(`${API}/share-meal`, { log_id: logId });
      alert(`Meal shared! Share link: ${window.location.origin}${res.data.share_url}`);
      fetchData();
    } catch (err) {
      console.error('Share error:', err);
    }
  };

  if (authLoading || loading) {
    return <div className="loading-screen"><div className="spinner" /><p>Loading...</p></div>;
  }

  if (!user) {
    return <AuthScreen onSuccess={() => {}} />;
  }

  return (
    <div className="app-container">
      {paymentResult && (
        <div
          className={`payment-result-banner ${paymentResult === 'success' ? 'payment-success' : 'payment-failed'}`}
          data-testid="payment-result-banner"
        >
          <span>
            {paymentResult === 'success'
              ? '🎉 Payment successful! Your Premium subscription is now active.'
              : paymentResult === 'invalid'
                ? 'Payment verification failed. If money was deducted, contact support.'
                : 'Payment failed or was cancelled. Please try again.'}
          </span>
          <button onClick={() => setPaymentResult(null)} data-testid="dismiss-payment-banner">✕</button>
        </div>
      )}
      {challengeJoinMsg && (
        <div className="payment-result-banner payment-success" data-testid="challenge-join-banner">
          <span>{challengeJoinMsg}</span>
          <button onClick={() => setChallengeJoinMsg(null)} data-testid="dismiss-challenge-banner">✕</button>
        </div>
      )}
      {currentPage === 'home' && (
        <Dashboard 
          onNavigate={setCurrentPage} 
          dailyProgress={dailyProgress} 
          tdeeData={tdeeData} 
          subscriptionStatus={subscriptionStatus}
          stats={stats}
        />
      )}
      {currentPage === 'scan' && (
        <Scanner 
          onFoodAnalyzed={() => { fetchData(); refreshUser(); setCurrentPage('home'); }} 
          onBack={() => setCurrentPage('home')}
          subscriptionStatus={subscriptionStatus}
        />
      )}
      {currentPage === 'profile' && (
        <Profile onSave={() => { fetchData(); setCurrentPage('home'); }} onBack={() => setCurrentPage('home')} onUpgrade={() => setCurrentPage('pricing')} />
      )}
      {currentPage === 'history' && (
        <History foodLogs={foodLogs} onDelete={handleDeleteLog} onShare={handleShareMeal} onBack={() => setCurrentPage('home')} />
      )}
      {currentPage === 'achievements' && (
        <Achievements onBack={() => setCurrentPage('home')} />
      )}
      {currentPage === 'leaderboard' && (
        <Leaderboard onBack={() => setCurrentPage('home')} onChallenge={() => setCurrentPage('challenges')} />
      )}
      {currentPage === 'pricing' && (
        <Pricing onBack={() => setCurrentPage('home')} />
      )}
      {currentPage === 'suggestions' && (
        <MealSuggestions onBack={() => setCurrentPage('home')} />
      )}
      {currentPage === 'reminders' && (
        <RemindersPage onBack={() => setCurrentPage('home')} onSaved={() => setReminderRefresh(k => k + 1)} />
      )}
      {currentPage === 'challenges' && (
        <ChallengesPage onBack={() => setCurrentPage('home')} />
      )}

      {!['scan', 'pricing'].includes(currentPage) && (
        <nav className="bottom-nav">
          <button className={`nav-item ${currentPage === 'home' ? 'active' : ''}`} onClick={() => setCurrentPage('home')} data-testid="nav-home-btn">
            <HomeIcon /><span>Home</span>
          </button>
          <button className={`nav-item ${currentPage === 'history' ? 'active' : ''}`} onClick={() => setCurrentPage('history')} data-testid="nav-history-btn">
            <HistoryIcon /><span>History</span>
          </button>
          <button className="nav-item scan-btn" onClick={() => setCurrentPage('scan')} data-testid="nav-scan-btn">
            <CameraIcon />
          </button>
          <button className={`nav-item ${currentPage === 'achievements' ? 'active' : ''}`} onClick={() => setCurrentPage('achievements')} data-testid="nav-badges-btn">
            <TrophyIcon /><span>Badges</span>
          </button>
          <button className={`nav-item ${currentPage === 'profile' ? 'active' : ''}`} onClick={() => setCurrentPage('profile')} data-testid="nav-profile-btn">
            <UserIcon /><span>Profile</span>
          </button>
        </nav>
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
